import { select, zoomTransform, event, axisRight } from "d3";
import {
  inRange,
  mouseDate,
  roundDate,
  zoomSetup
} from "@/_core/charts/TechnicalAnalysis";
import { displayDataCOT } from "@/_core/charts/CommitmentTraders";

export function D3HoverZoom({
  _this,
  D3Chart,
  data,
  auxName,
  hoverParents = [],
  methodMinMax = "",
  dataSliced = "",
  barWidth = 0.7
}) {
  const svg = D3Chart.svg;
  function mouseover() {
    if (_this.transform === undefined || _this.transform == null) {
      _this.transform = zoomTransform(
        select(".hoverParent" + auxName)._groups[0][0]
      );
    }
    event.preventDefault();

    const xPixels = Math.max(
      Math.min(event.layerX - svg.margin.left, svg.width),
      0
    );
    let xt = _this.transform.rescaleX(svg.x);
    let dat = roundDate(xt.invert(xPixels));
    let selData;

    let highlightCandles = true;
    if (dat <= data.at(-1).date) {
      selData = mouseDate(xt.invert(xPixels), data);
    } else {
      selData = JSON.parse(JSON.stringify(data.at(-1)));
      selData["date"] = dat;
      highlightCandles = false;
    }
    const yPixels = Math.max(
      Math.min(event.layerY - svg.margin.top, svg.height),
      0
    );

    let xPix = xt(selData.date);

    select(".vHoverLineNet")
      .style("opacity", 1)
      .attr("x1", xPix)
      .attr("x2", xPix);

    let bandWidth = _this.transform.k * _this.bandWidth;
    const candleWidth = bandWidth * barWidth + 1;

    // Showing elements
    for (let hoverElement of D3Chart.hoverElements) {
      hoverElement.show({
        yPixels,
        xPix,
        selData,
        dateFormat: _this.dateFormat,
        xt,
        candleWidth,
        highlightCandles,
        className: hoverElement.className
      });
    }
    const yt2 = _this.svgNode.y;
    // Show hereditary hovers
    for (let hereditaryHover of D3Chart.hereditaryHovers) {
      hereditaryHover.show({
        yPixels,
        yt: yt2,
        xPix,
        selData,
        dataYt: selData,
        dateFormat: _this.dateFormat,
        xt,
        candleWidth,
        keyYt: "close",
        highlightCandles
      });
    }

    if (highlightCandles) {
      const yt3 = _this.svgNodeNet.y;

      if (_this.userSelections["showComm"]) {
        select(".barCHistogram")
          .style("opacity", 1)
          .attr("x", xPix - candleWidth / 2)
          .attr("y", Math.min(yt3(0), yt3(selData["commercial_net"])))
          .attr(
            "height",
            Math.max(1, Math.abs(yt3(0) - yt3(selData["commercial_net"])))
          )
          .attr("width", candleWidth);
      }
      if (_this.userSelections["showNonComm"]) {
        select(".barNCHistogram")
          .style("opacity", 1)
          .attr("x", xPix - candleWidth / 2)
          .attr("y", Math.min(yt3(0), yt3(selData["non_commercial_net"])))
          .attr(
            "height",
            Math.max(1, Math.abs(yt3(0) - yt3(selData["non_commercial_net"])))
          )
          .attr("width", candleWidth);
      }
      if (_this.userSelections["showNonRep"]) {
        select(".barNRHistogram")
          .style("opacity", 1)
          .attr("x", xPix - candleWidth / 2)
          .attr("y", Math.min(yt3(0), yt3(selData["non_rep_net"])))
          .attr(
            "height",
            Math.max(1, Math.abs(yt3(0) - yt3(selData["non_rep_net"])))
          )
          .attr("width", candleWidth);
      }
    } else {
      if (_this.userSelections["showComm"]) {
        select(".barCHistogram").style("opacity", 0);
      }
      if (_this.userSelections["showNonComm"]) {
        select(".barNCHistogram").style("opacity", 0);
      }
      if (_this.userSelections["showNonRep"]) {
        select(".barNRHistogram").style("opacity", 0);
      }
    }
    displayDataCOT(selData, _this);
  }
  function mouseout() {
    if (_this.userSelections["showComm"]) {
      select(".barCHistogram").style("opacity", 0);
    }
    if (_this.userSelections["showNonComm"]) {
      select(".barNCHistogram").style("opacity", 0);
    }
    if (_this.userSelections["showNonRep"]) {
      select(".barNRHistogram").style("opacity", 0);
    }
    select(".vHoverLineNet").style("opacity", 0);

    // Hiding elements
    for (let hoverElement of D3Chart.hoverElements) {
      hoverElement.hide();
    }
    // Hiding hereditary hovers
    for (let hereditaryHover of D3Chart.hereditaryHovers) {
      hereditaryHover.hide();
    }
    displayDataCOT(_this.allData[dataSliced].at(-1), _this);
  }
  function zoomed() {
    if (!_this.keepDateInterval) {
      setTimeout(() => {
        _this.pickedDateInterval = "-1";
      }, 500);
    }

    if (_this.userSelections["showComm"]) {
      select(".barCHistogram").style("opacity", 0);
    }
    if (_this.userSelections["showNonComm"]) {
      select(".barNCHistogram").style("opacity", 0);
    }
    if (_this.userSelections["showNonRep"]) {
      select(".barNRHistogram").style("opacity", 0);
    }
    select(".vHoverLineNet").style("opacity", 0);

    // Hiding elements
    for (let hoverElement of D3Chart.hoverElements) {
      hoverElement.hide();
    }
    // Hiding hereditary hovers
    for (let hereditaryHover of D3Chart.hereditaryHovers) {
      hereditaryHover.hide();
    }

    _this.transform = event.transform;
    svg.gX.call(svg.xAxis.scale(_this.transform.rescaleX(svg.x)));
    const xt = _this.transform.rescaleX(svg.x);
    const yt = svg.y;
    handleZooming(_this.transform, xt, yt, _this, barWidth, D3Chart);

    // transform other charts
    if (_this.externalZoom === false) {
      _this.externalZoom = true;
      for (let hoverParent of hoverParents) {
        select(`.hoverParent${hoverParent}`).call(
          _this[`zoom${hoverParent}`].transform,
          _this.transform
        );
      }
      _this.externalZoom = false;
    }
  }
  function zoomEnd() {
    _this.transform = event.transform;
    const xt = _this.transform.rescaleX(svg.x);
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function() {
      let xmin = new Date(xt.invert(0));
      let xmax = new Date(xt.invert(svg.width));

      if (!_this.externalZoom) {
        let dataD = data.map(d => d);
        _this.allData[dataSliced] = dataD.filter(function(d) {
          return inRange(d.date, xmin, xmax);
        });
      }
      let minMaxYAxis, minMaxYAxis2;
      minMaxYAxis = _this[methodMinMax](_this.allData[dataSliced]);
      minMaxYAxis = [minMaxYAxis[0], minMaxYAxis[1]];
      if (minMaxYAxis[2]) {
        minMaxYAxis2 = [minMaxYAxis[2], minMaxYAxis[3]];
        if (!isFinite(minMaxYAxis2[0])) {
          minMaxYAxis2[0] = -1;
        }
        if (!isFinite(minMaxYAxis2[1])) {
          minMaxYAxis2[1] = 1;
        }
      }
      if (!isFinite(minMaxYAxis[0])) {
        minMaxYAxis[0] = -1;
      }
      if (!isFinite(minMaxYAxis[1])) {
        minMaxYAxis[1] = 1;
      }
      svg.y.domain(minMaxYAxis);
      let yt = svg.y;

      handleZoomEnd(
        _this.transform,
        xt,
        yt,
        _this.allData[dataSliced],
        _this,
        barWidth,
        D3Chart
      );
    }, 200);
  }

  let resizeTimer;
  zoomSetup(
    _this,
    "svgNode" + auxName,
    "zoom" + auxName,
    auxName,
    "",
    mouseover,
    mouseout,
    zoomed,
    zoomEnd
  );
}

function handleZooming(t, xt, yt, _this, barWidth = 0.7, D3Chart) {
  let bandWidth = t.k * _this.bandWidth;
  const candleWidth = bandWidth * barWidth + 1;
  D3Chart.svg["OIHist"]
    .attr("x", d => xt(d.date) - candleWidth / 2)
    .attr("y", d => Math.min(yt(0), yt(d["open_interest"])))
    .attr("width", candleWidth);

  D3Chart.svg["zeroLineOI"].attr("y1", yt(0)).attr("y2", yt(0));
}

function handleZoomEnd(t, xt, yt, filtered, _this, barWidth = 0.7, D3Chart) {
  let bandWidth = t.k * _this.bandWidth;
  const candleWidth = bandWidth * barWidth + 1;
  // Recalculate zoom elements
  for (let zoomElement of _this.D3ChartOI.zoomElements) {
    zoomElement.recalculate({
      data: filtered,
      candleWidth,
      yt,
      xt,
      customNameBar: "OIHist"
    });
  }
  D3Chart.svg["zeroLineOI"]
    .attr("y1", D3Chart.svg.y(0))
    .attr("y2", D3Chart.svg.y(0));

  let tickCount = D3Chart.svg.height / 40;
  D3Chart.svg.gY
    .transition()
    .duration(500)
    .call(
      axisRight()
        .tickSize(-D3Chart.svg.width)
        .ticks(tickCount)
        .tickSizeOuter(0)
        .tickPadding(10)
        .ticks(tickCount)
        .scale(yt)
    );
}
